import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Primitive } from '@sentry/types';
import { Subscription } from 'rxjs';
import {
  TabVisibilityService,
  wilsonCancelIdleCallback,
  wilsonRequestIdleCallback,
} from '@wilson/utils';

// WILP-8787: diagnostic instrumentation to confirm/quantify the suspected
// Shift Timeline memory leak before deciding whether to invest in a fix.
const SAMPLE_INTERVAL_MS = 30_000;
const BACKGROUND_STOP_DELAY_MS = 5 * 60_000;
const CHECKPOINTS_MB = [500, 750, 1000, 1250];
const ALERT_THRESHOLD_MB = 750;

@Injectable()
export class TimelineMemoryMonitorService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly tabVisibilityService = inject(TabVisibilityService);
  private getTags: () => Record<string, Primitive> = () => ({});

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private pendingIdleCallbackIds = new Set<
    ReturnType<typeof wilsonRequestIdleCallback>
  >();
  private backgroundStopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private visibilitySubscription: Subscription | null = null;

  private crossed = new Set<number>();
  private alerted = false;
  // highest checkpoint already sent to Sentry via a real event (not just a breadcrumb)
  private highestReportedMB = 0;
  private startedAt = 0;
  private started = false;

  private pageHideHandler = (): void => {
    this.stop();
  };

  private static isSupported(): boolean {
    return typeof performance !== 'undefined' && 'memory' in performance;
  }

  start(getTags: () => Record<string, Primitive>): void {
    if (!TimelineMemoryMonitorService.isSupported() || this.started) {
      return;
    }
    this.getTags = getTags;
    this.started = true;
    this.startedAt = performance.now();
    // isTabVisible$ is a BehaviorSubject, so this immediately triggers sampling if the tab is visible
    this.visibilitySubscription =
      this.tabVisibilityService.isTabVisible$.subscribe((isVisible) =>
        this.onVisibilityChange(isVisible),
      );
    // pagehide fires on real tab/window close (and is bfcache-safe), unlike ngOnDestroy
    window.addEventListener('pagehide', this.pageHideHandler);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    window.removeEventListener('pagehide', this.pageHideHandler);
    this.visibilitySubscription?.unsubscribe();
    this.visibilitySubscription = null;
    this.clearBackgroundStopTimeout();
    this.stopSampling();
    this.flushPeakIfNeeded();
  }

  private onVisibilityChange(isVisible: boolean): void {
    if (isVisible) {
      this.clearBackgroundStopTimeout();
      if (!this.intervalId) {
        this.startSampling();
      }
    } else if (!this.backgroundStopTimeoutId) {
      this.backgroundStopTimeoutId = setTimeout(
        () => this.stopSampling(),
        BACKGROUND_STOP_DELAY_MS,
      );
    }
  }

  private clearBackgroundStopTimeout(): void {
    if (this.backgroundStopTimeoutId) {
      clearTimeout(this.backgroundStopTimeoutId);
      this.backgroundStopTimeoutId = null;
    }
  }

  private startSampling(): void {
    this.zone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        const idleCallbackId = wilsonRequestIdleCallback(() => {
          this.pendingIdleCallbackIds.delete(idleCallbackId);
          this.sample();
        });
        this.pendingIdleCallbackIds.add(idleCallbackId);
      }, SAMPLE_INTERVAL_MS);
    });
  }

  private stopSampling(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    for (const idleCallbackId of this.pendingIdleCallbackIds) {
      wilsonCancelIdleCallback(idleCallbackId);
    }
    this.pendingIdleCallbackIds.clear();
  }

  private sample(): void {
    const memory = (
      performance as unknown as { memory: { usedJSHeapSize: number } }
    ).memory;
    const usedMB = memory.usedJSHeapSize / 1024 / 1024;
    const extra = {
      usedMB: Math.round(usedMB),
      elapsedMinutes: Math.round((performance.now() - this.startedAt) / 60_000),
    };
    const tags = this.getTags();

    for (const mb of CHECKPOINTS_MB) {
      if (usedMB >= mb && !this.crossed.has(mb)) {
        this.crossed.add(mb);
        Sentry.addBreadcrumb({
          category: 'timeline.memory',
          message: `Heap crossed ${mb}MB`,
          level: 'info',
          data: { ...extra, ...tags },
        });
      }
    }

    if (usedMB >= ALERT_THRESHOLD_MB && !this.alerted) {
      this.alerted = true;
      this.highestReportedMB = ALERT_THRESHOLD_MB;
      Sentry.captureMessage('Shift Timeline high memory usage', {
        level: 'warning',
        tags,
        extra,
      });
    }
  }

  // Sessions that keep climbing past the alert threshold only add breadcrumbs (no quota
  // cost) for the higher checkpoints. Report the actual peak once, when the page tears down,
  // so the full growth curve is captured without billing an event per checkpoint.
  private flushPeakIfNeeded(): void {
    if (!this.alerted || this.crossed.size === 0) {
      return;
    }
    const highestCrossed = Math.max(...this.crossed);
    if (highestCrossed <= this.highestReportedMB) {
      return;
    }
    this.highestReportedMB = highestCrossed;
    Sentry.captureMessage('Shift Timeline high memory usage (session end)', {
      level: 'warning',
      tags: this.getTags(),
      extra: {
        peakUsedMB: highestCrossed,
        elapsedMinutes: Math.round(
          (performance.now() - this.startedAt) / 60_000,
        ),
      },
    });
  }
}
